import { useEffect, useState } from 'react';
import { SubdomainField } from '@shared/components/SubdomainField';
import { useUserStore } from '../../../auth/store/userStore';
import { MAX_SUBDOMAIN_LENGTH } from '@shared/utils/subdomain';
import { SUBDOMAIN_BASE_DOMAIN } from '@shared/constants/config';
import { useSubdomainAvailability } from '@shared/hooks/useSubdomainAvailability';

/**
 * Company Subdomain — controlled input for viewing and editing the company subdomain.
 * Reuses the shared format-feedback util + the availability check; the server re-validates
 * and enforces uniqueness on save.
 *
 * Multi-site note: the subdomain is a CUSTOMER-level attribute (one subdomain per `Firm`, shared
 * by every site that customer owns). It must NOT be made per-site — the whole SPA uses the
 * subdomain to resolve the tenant pre-auth and to route between customers. Site switching happens
 * inside the subdomain (same host, different active-site cookie), so this section stays unchanged
 * under multi-site.
 */
export function FirmSubdomainSection({onSubdomainChange, initialValue, onStatusChange, onErrorChange }) {
  const { profile } = useUserStore();
  
  // Track whether user has explicitly edited the field (vs. just viewing the initial value)
  const [isDirty, setIsDirty] = useState(false);
  
  // Local edit state: tracks user's current input (empty string means user cleared it, not "untouched")
  const [editValue, setEditValue] = useState('');
  
  // Determine the current loaded value (SSO config or profile)
  // This is recomputed whenever initialValue changes, so it's always current
  const currentLoadedValue = initialValue !== undefined ? (initialValue ?? '') : (profile?.firm?.subdomain ?? '');
  
  // Display value: show user's edits if they've touched the field, otherwise show loaded value
  const displayValue = isDirty ? editValue : currentLoadedValue;

  // When user types, mark as dirty and update editValue
  const handleChange = (e) => {
    setIsDirty(true);
    setEditValue(e.target.value);
  };

  // Notify parent component of subdomain changes (only when dirty, to avoid sending stale values)
  useEffect(() => {
    if (isDirty) {
      onSubdomainChange?.(editValue);
    }
  }, [editValue, isDirty, onSubdomainChange]);

  // Debounced format + availability check (shared with the signup form). Skip only when the value
  // is unchanged from the currently loaded value. This ensures availability checks run when the user edits.
  const { status, error } = useSubdomainAvailability(displayValue, { skip: displayValue === currentLoadedValue });

  // Notify parent of validation status changes (e.g., for disabling save button)
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // Notify parent of error changes
  useEffect(() => {
    const displayError = error || (status === 'taken' ? 'This subdomain is already taken.' : undefined);
    onErrorChange?.(displayError);
  }, [error, status, onErrorChange]);

  const displayError = error || (status === 'taken' ? 'This subdomain is already taken.' : undefined);

  // Clearing the field is allowed — an empty value maps to NULL in the DB and removes the
  // firm's branded subdomain URL. Availability check is skipped for empty (nothing to check).
  return (
    <div className="space-y-1.5" data-testid="profile-firm-subdomain-section">
      <SubdomainField
        id="firmSubdomain"
        label="Company Domain"
        baseDomain={SUBDOMAIN_BASE_DOMAIN}
        value={displayValue}
        onChange={handleChange}
        maxLength={MAX_SUBDOMAIN_LENGTH}
        error={displayError}
        inputClassName="bg-surface-field border-line-input text-fg"
        dataTestId="profile-firm-subdomain"
      >
        {!error && status === 'checking' && (
          <p className="text-xs text-fg-muted">Checking availability...</p>
        )}
        {!error && status === 'available' && (
          <p className="text-xs text-success-fg">Subdomain is available.</p>
        )}
      </SubdomainField>

      {/* Outside SubdomainField so it renders below the address line, not between the
          status text and the address (children render above the preview). */}
      <p className="text-xs text-fg-muted">
        This is your company&apos;s unique address. Only lowercase letters and numbers, up to {MAX_SUBDOMAIN_LENGTH} characters.
      </p>
    </div>
  );
}
